import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useMainForgotPassword } from '@/hooks/main/useMainAuth';
import {
  forgotPasswordFormSchema,
  type ForgotPasswordFormData,
} from '@/schemas/forgotPasswordSchema';
import { AppButton, AppForm, AppTextField } from '@/components/atoms';

/**
 * Forgot Password Page Component
 *
 * Allows users to request a password reset email.
 * Matches RA2 design; uses theme for button/link colors; success/errors on the form (no toast).
 */
const ForgotPasswordPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { colors } = useTheme();
  const [isMailSent, setIsMailSent] = useState(false);
  const [sentMessage, setSentMessage] = useState('');
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);

  const forgotPasswordMutation = useMainForgotPassword();
  const isLoading = forgotPasswordMutation.isPending;

  const INVALID_CLINIC_OR_USER_MESSAGE = '治療院IDもしくはユーザーIDが不正です';
  const isInvalidClinicOrUserMessage = (message?: string | null) => {
    if (!message) return false;
    return (
      message.includes(INVALID_CLINIC_OR_USER_MESSAGE) ||
      message.includes('治療院IDが見つかりません') ||
      message.includes('ユーザIDが見つかりません')
    );
  };
  const isMedixFamily = site === 'medix' || site === 'upsystem';
  const NO_REPLY_EMAIL_MESSAGE = isMedixFamily
    ? '返信用メールアドレスが設定されていません。お手数ですが【03-3255-1095 審査部】までお問い合わせください'
    : '返信用メールアドレスが設定されていません。お手数ですが【システム管理者】までお問い合わせください';
  const isNoReplyEmailMessage = (message?: string | null) => {
    if (!message) return false;
    return (
      message.includes('このアカウントにはメールアドレスが設定されていません') ||
      message.includes('メールアドレスが設定されていません')
    );
  };
  const WRONG_SITE_TYPE_MESSAGE = 'この治療院IDは当サイトではご利用いただけません。';
  const isWrongSiteTypeMessage = (message?: string | null) => {
    if (!message) return false;
    return message.includes('当サイトではご利用いただけません');
  };

  const {
    control,
    handleSubmit,
    setError: setFormError,
    clearErrors,
  } = useTrimmedForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      clinic_number: '',
      login_id: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    clearErrors();
    setIsMailSent(false);
    setFormErrorMessage(null);

    try {
      const response = await forgotPasswordMutation.mutateAsync({
        site: site || 'medix',
        clinicNumber: data.clinic_number,
        loginId: data.login_id,
      });

      if (response.success) {
        const msg = response.message || 'パスワードリセット用のメールを送信いたしました。';
        setSentMessage(msg);
        setIsMailSent(true);
      } else {
        const message = response.message || 'エラーが発生しました。';
        if (isWrongSiteTypeMessage(message)) {
          setFormErrorMessage(WRONG_SITE_TYPE_MESSAGE);
        } else if (isInvalidClinicOrUserMessage(message)) {
          setFormError('login_id', { type: 'server', message: INVALID_CLINIC_OR_USER_MESSAGE });
        } else if (isNoReplyEmailMessage(message)) {
          setFormErrorMessage(NO_REPLY_EMAIL_MESSAGE);
        } else {
          setFormErrorMessage(message);
        }
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        const message = axiosError.response?.data?.message || 'エラーが発生しました。';
        if (isWrongSiteTypeMessage(message)) {
          setFormErrorMessage(WRONG_SITE_TYPE_MESSAGE);
        } else if (isInvalidClinicOrUserMessage(message)) {
          setFormError('login_id', { type: 'server', message: INVALID_CLINIC_OR_USER_MESSAGE });
        } else if (isNoReplyEmailMessage(message)) {
          setFormErrorMessage(NO_REPLY_EMAIL_MESSAGE);
        } else {
          setFormErrorMessage(message);
        }
      } else {
        setFormErrorMessage('エラーが発生しました。');
      }
    }
  };

  return (
    <div className="login-page-bg min-h-screen flex items-center justify-center">
      <div className="w-full px-4 max-w-[460px] mt-2.5">
        <div className="text-center mb-3">
          <img
            src="/images/login/logo.png"
            alt="RA2N Logo"
            className="mx-auto w-[380px] max-w-full"
          />
        </div>

        <div className="bg-white bg-opacity-95 rounded-lg shadow-lg py-6 px-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            パスワードリセット
          </h2>

          {isMailSent && (
            <div
              role="status"
              className="mb-4 rounded border border-green-600 bg-green-50 px-3 py-2.5 text-left text-sm leading-relaxed text-green-900"
            >
              {sentMessage}
            </div>
          )}

          {!isMailSent && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              {formErrorMessage && (
                <div
                  role="alert"
                  className="rounded border border-red-600 bg-red-50 px-3 py-2.5 text-left text-sm leading-relaxed text-red-900"
                >
                  {formErrorMessage}
                </div>
              )}
              <Controller
                name="clinic_number"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    validateStatus={fieldState.error ? 'error' : undefined}
                    help={fieldState.error?.message}
                    className="mb-0"
                  >
                    <AppTextField
                      {...field}
                      id="clinic_number"
                      placeholder="治療院ID"
                      status={fieldState.error ? 'error' : undefined}
                      className="w-full"
                      autoComplete="username"
                      data-testid="clinic-number-input"
                      autoFocus
                    />
                  </AppForm.Item>
                )}
              />

              <Controller
                name="login_id"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    validateStatus={fieldState.error ? 'error' : undefined}
                    help={fieldState.error?.message}
                    className="mb-0"
                  >
                    <AppTextField
                      {...field}
                      id="login_id"
                      placeholder="ユーザーID"
                      status={fieldState.error ? 'error' : undefined}
                      className="w-full"
                      autoComplete="username"
                      data-testid="login-id-input"
                    />
                  </AppForm.Item>
                )}
              />

              <div className="flex justify-center pt-3">
                <AppButton
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={isLoading}
                  className="py-2 px-6 text-sm"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="submit-button"
                >
                  {isLoading ? '送信中...' : 'パスワードリセット用メールの送信'}
                </AppButton>
              </div>
            </form>
          )}

          <div className="text-center mt-4">
            <Link
              to={`/${site || 'medix'}/login`}
              className="text-sm hover:opacity-80"
              style={{ color: colors.primary }}
            >
              ログイン画面に戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
